import React, { useEffect, useState, useCallback } from "react";
import { Download, X } from "lucide-react";
import { toast } from "react-toastify";
import style from "./installPwaButton.module.scss";

/**
 * Floating button to install PWA on supported mobile browsers.
 * Shows only when:
 * - The browser fires `beforeinstallprompt`
 * - Likely a mobile viewport
 */
const InstallPwaButton = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);

  const isMobile = () => {
    // Heuristic: touch + narrow viewport or userAgent match
    const mq = window.matchMedia && window.matchMedia("(max-width: 900px)").matches;
    const touch = "ontouchstart" in window || navigator.maxTouchPoints > 0;
    const ua = navigator.userAgent || navigator.vendor || window.opera;
    const mobileUA = /android|iphone|ipad|ipod|iemobile|wpdesktop|mobile/i.test(ua);
    return (mq && touch) || mobileUA;
  };

  useEffect(() => {
    const handleBeforeInstall = (e) => {
      // Prevent the mini-infobar on mobile
      e.preventDefault();
      setDeferredPrompt(e);
      if (isMobile()) {
        setVisible(true);
      }
    };

    const handleInstalled = () => {
      setVisible(false);
      setDeferredPrompt(null);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstall);
    window.addEventListener("appinstalled", handleInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstall);
      window.removeEventListener("appinstalled", handleInstalled);
    };
  }, []);

  const onInstallClick = useCallback(async () => {
    try {
      if (!deferredPrompt) return;
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === "accepted") {
        toast.success("Installation started");
      } else {
        toast.info("Installation dismissed");
      }
      // Reset so button hides until next event
      setDeferredPrompt(null);
      setVisible(false);
    } catch (err) {
      toast.error("Unable to start installation");
    }
  }, [deferredPrompt]);

  if (!visible) return null;

  return (
    <div className={style.bannerStyle} role="region" aria-label="Install Orderly">
      <button
        type="button"
        onClick={onInstallClick}
        aria-label="Install app"
        title="Install app"
        className={style.installBtn}
      >
        <Download size={20} />
        <span>Click here to install the app</span>
      </button>
      <button
        type="button"
        aria-label="Close install banner"
        onClick={(e) => {
          e.stopPropagation();
          setVisible(false);
          setDeferredPrompt(null);
        }}
        className={style.closeBtn}
      >
        <X />
      </button>
    </div>
  );
};

export default InstallPwaButton;
